import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const inputVariants = cva(
	"flex w-full rounded-[4px] border border-input bg-background text-neutral-600 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-neutral-900 placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-field-focus-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500 read-only:cursor-not-allowed read-only:bg-neutral-100 read-only:text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900 dark:ring-offset-neutral-900 dark:file:text-neutral-50 dark:placeholder:text-neutral-400 dark:focus-visible:ring-neutral-300",
	{
		variants: {
			size: {
				default: "h-8 px-3 py-2 text-[12px] md:text-sm",
				lg: "h-12 px-4 py-2 text-lg",
			},
		},
		defaultVariants: {
			size: "default",
		},
	},
);

/** Props for the {@link Input} component. Extends native `<input>` attributes with an optional invalid state and size variant. */
export type InputProps = Omit<React.ComponentProps<"input">, "size"> &
	VariantProps<typeof inputVariants> & {
		/** When true, applies a danger ring and sets `aria-invalid` on the underlying `<input>`. */
		isInvalid?: boolean;
	};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	(
		{
			className,
			type,
			size,
			isInvalid = false,
			"aria-invalid": ariaInvalid,
			...props
		},
		ref,
	) => (
		<input
			type={type}
			className={cn(
				inputVariants({ size }),
				"aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-danger-400",
				className,
			)}
			ref={ref}
			{...props}
			aria-invalid={ariaInvalid ?? (isInvalid || undefined)}
		/>
	),
);
Input.displayName = "Input";

export { Input, inputVariants };
